import type { InputState } from "../../input/state";
import { $chatOpen } from "../../state/chat";
import { $panel } from "../../state/stores";
import { $touchMode } from "../../state/touch";
import { TouchRouter } from "./router";

/**
 * The full-screen layer under the touch buttons (spec 2p §3.4). It goes first in the HUD so every later
 * element (buttons, hotbar, open chat) stacks above it, and it steps aside while a panel is open so the
 * inspector's camera gets the fingers (Review Focus 4).
 */
export function mountTouchLayer(root: HTMLElement, input: InputState): { reset(): void } {
  const layer = document.createElement("div");
  layer.className = "touch-layer";
  const base = document.createElement("div");
  base.className = "stick-base";
  const knob = document.createElement("div");
  knob.className = "stick-knob";
  base.appendChild(knob);
  layer.appendChild(base);
  root.prepend(layer);

  const draw = () => {
    const s = router.stick;
    base.classList.toggle("show", !!s);
    if (!s) return;
    base.style.left = `${s.x}px`;
    base.style.top = `${s.y}px`;
    knob.style.transform = `translate(${s.dx}px, ${s.dy}px)`;
  };
  const router = new TouchRouter(input, () => window.innerWidth, draw);

  layer.addEventListener("pointerdown", (e) => {
    e.preventDefault();
    // With chat open a tap on the world closes it, as a click does on desktop: the input's blur handler
    // keeps the draft. The finger drives nothing, so it cannot walk on once the chat is gone.
    if ($chatOpen.get()) { (document.activeElement as HTMLElement | null)?.blur?.(); return; }
    router.down(e);
  });
  layer.addEventListener("pointermove", (e) => router.move(e));
  layer.addEventListener("pointerup", (e) => router.up(e));
  layer.addEventListener("pointercancel", () => router.reset());
  window.addEventListener("blur", () => router.reset());

  const sync = () => {
    const off = !$touchMode.get() || !!$panel.get();
    layer.classList.toggle("off", off);
    if (off) router.reset();
  };
  $touchMode.subscribe(sync);
  $panel.subscribe(sync);
  $chatOpen.subscribe((open) => { if (open) router.reset(); });
  return { reset: () => router.reset() };
}
